import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink } from "lucide-react";

import {
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Mono,
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@lagoon/ui";

import { api, ApiError, uploadArchive } from "../api.js";
import { isEmailUnverified, VerifyEmailNotice } from "../components/verify-email.js";
import { Failure, LoadingPanel, Notice, Working } from "../components/feedback.js";
import { Activity } from "../components/activity.js";
import { Domains } from "../components/domains.js";
import { SiteExport } from "../components/export.js";
import { Notifications } from "../components/notifications.js";
import { Passes } from "../components/passes.js";
import { Releases } from "../components/releases.js";
import { Allowlist } from "../components/allowlist.js";
import { SiteGroups } from "../components/site-groups.js";
import { Link } from "../router.js";

/**
 * One site.
 *
 * Tabbed rather than one long scroll. The page used to stack deploy, releases
 * and the allowlist together, which was fine for three panels and stopped
 * being fine once an owner could also see who had read the site, hand out
 * guest links and configure notifications.
 *
 * The tab is in the URL, so a link to the activity log is a link and a refresh
 * does not quietly send someone back to the first tab. The tab strip is now the
 * library's Radix `Tabs`, which is why the triggers are buttons rather than
 * anchors: Radix owns the roving tabindex and the `aria-controls` pairing, and
 * a tab that is also a link cannot have both without one of them lying. The URL
 * still moves with the selection, so linking and refreshing behave exactly as
 * before.
 */

const TABS = [
  { id: "deploy", label: "Deploy" },
  { id: "people", label: "People" },
  { id: "activity", label: "Activity" },
  { id: "settings", label: "Settings" },
] as const;

export function SiteDetail(props: {
  siteId: string;
  tab: string;
  navigate: (to: string) => void;
  sitesDomain: string;
  scheme: string;
  /** Threaded in rather than spelled out: the product may be renamed. */
  supportEmail: string;
}): React.ReactElement {
  const queryClient = useQueryClient();

  const site = useQuery({
    queryKey: ["site", props.siteId],
    queryFn: () => api.getSite(props.siteId),
    /**
     * Poll only while something is genuinely running.
     *
     * `pending` is deliberately excluded. That state is written when the
     * upload URL is issued, before any bytes exist, so an abandoned upload
     * stays pending forever and the old predicate held the page in a 1.5
     * second refetch loop for as long as it was open, on every future visit
     * too.
     */
    refetchInterval: (query) => {
      const deploys = query.state.data?.deploys ?? [];
      return deploys.some((d) => d.state === "processing") ? 1500 : false;
    },
  });

  if (site.isPending) {
    return <LoadingPanel label="Loading this site" />;
  }
  if (site.isError) {
    return (
      <div className="lg:flex lg:flex-col lg:items-start lg:gap-4">
        <Failure error={site.error} fallback="Could not load this site." />
        <Button variant="outline" asChild>
          <a href="/">Back to sites</a>
        </Button>
      </div>
    );
  }

  const { site: record, deploys } = site.data;
  const url = `${props.scheme}://${record.slug}.${props.sitesDomain}`;
  const tab = TABS.some((t) => t.id === props.tab) ? props.tab : "deploy";
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["site", props.siteId] });
  };

  return (
    <div className="lg:flex lg:flex-col lg:gap-7">
      <PageHeader
        breadcrumb={
          <Link
            to="/"
            navigate={props.navigate}
            className="lg:inline-flex lg:items-center lg:gap-2 lg:font-label lg:text-eyebrow lg:uppercase lg:tracking-eyebrow lg:text-muted-foreground"
          >
            <ArrowLeft aria-hidden="true" className="lg:size-3.5" />
            Sites
          </Link>
        }
        title={record.name}
        description={
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="lg:inline-flex lg:items-center lg:gap-2 lg:font-mono lg:text-body-sm"
          >
            {record.slug}.{props.sitesDomain}
            <ExternalLink aria-hidden="true" className="lg:size-4" />
          </a>
        }
        actions={
          record.publishedDeployId === null ? (
            <Badge tone="neutral">not published</Badge>
          ) : (
            <Badge tone="success">live</Badge>
          )
        }
      />

      <Tabs
        value={tab}
        onValueChange={(next) => {
          props.navigate(`/sites/${props.siteId}/${next}`);
        }}
      >
        <TabsList aria-label="Site sections">
          {TABS.map((entry) => (
            <TabsTrigger key={entry.id} value={entry.id}>
              {entry.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="deploy">
          <div className="lg:flex lg:flex-col lg:gap-5">
            <Deployer siteId={props.siteId} supportEmail={props.supportEmail} onDone={refresh} />
            <Releases siteId={props.siteId} deploys={deploys} onChanged={refresh} />
          </div>
        </TabsContent>

        <TabsContent value="people">
          <div className="lg:flex lg:flex-col lg:gap-5">
            <Allowlist siteId={props.siteId} />
            <SiteGroups siteId={props.siteId} navigate={props.navigate} />
            <Domains siteId={props.siteId} />
            <Passes siteId={props.siteId} />
          </div>
        </TabsContent>

        <TabsContent value="activity">
          <Activity siteId={props.siteId} />
        </TabsContent>

        <TabsContent value="settings">
          <div className="lg:flex lg:flex-col lg:gap-5">
            <Notifications siteId={props.siteId} navigate={props.navigate} />
            <SiteExport site={record} deploys={deploys} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * Uploading a release.
 *
 * Three steps, and the middle one deliberately does not touch this API: the
 * archive goes straight to Blob on a short-lived SAS URL, so it never crosses
 * Front Door and is not subject to its body cap, timeout or egress meter.
 */
function Deployer(props: {
  siteId: string;
  supportEmail: string;
  onDone: () => void;
}): React.ReactElement {
  const fileInput = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<string | null>(null);
  /**
   * The caught failure, not a sentence.
   *
   * It used to be the rendered string, which was enough while every failure
   * rendered as one line of red text. An unverified address does not: it wants
   * a heading and a control, so the decision about how to render has to happen
   * where JSX can be returned, and that means holding the error itself.
   */
  const [failure, setFailure] = useState<unknown>(null);

  const deploy = useMutation({
    mutationFn: async (file: File) => {
      setFailure(null);
      setStage("Requesting an upload URL…");
      const ticket = await api.createUpload(props.siteId);

      setStage("Uploading to storage…");
      await uploadArchive(ticket, file);

      setStage("Extracting…");
      await api.processDeploy(ticket.deployId);
      return ticket.deployId;
    },
    onSuccess: () => {
      setStage(null);
      if (fileInput.current) fileInput.current.value = "";
      props.onDone();
    },
    onError: (caught) => {
      setStage(null);
      setFailure(caught);
    },
  });

  return (
    <Card interactive={false}>
      <div className="lg:flex lg:flex-col lg:gap-5">
        <CardHeader>
          <CardTitle as="h2">Deploy</CardTitle>
          <CardDescription>
            A ZIP of your static site, with <Mono>index.html</Mono> at the top level of the archive.
            Uploading does not publish: you choose when it goes live.
          </CardDescription>
        </CardHeader>

        {failure === null ? null : isEmailUnverified(failure) ? (
          <VerifyEmailNotice supportEmail={props.supportEmail} />
        ) : (
          <Notice tone="danger">{explainUploadFailure(failure)}</Notice>
        )}

        <div className="lg:flex lg:flex-wrap lg:items-center lg:gap-4">
          <Input
            ref={fileInput}
            type="file"
            accept=".zip,application/zip"
            disabled={deploy.isPending}
            aria-label="Release archive"
            className="lg:flex-1"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) deploy.mutate(file);
            }}
          />
          {stage === null ? null : <Working label={stage} />}
        </div>
      </div>
    </Card>
  );
}

/**
 * Turn an upload failure into something actionable.
 *
 * These used to collapse into "The deploy failed." or a bare status number.
 * The two that actually happen have specific, different remedies: the upload
 * link is good for fifteen minutes and a large archive on a slow connection
 * can outlive it, and a browser that cannot reach storage at all fails with a
 * TypeError and nothing in the server logs.
 */
function explainUploadFailure(caught: unknown): string {
  if (caught instanceof ApiError) {
    if (caught.code === "upload_failed" && (caught.status === 403 || caught.status === 400)) {
      return "The upload link expired or was rejected by storage. Upload windows last 15 minutes; choose the file again to start over.";
    }
    return caught.message;
  }
  if (caught instanceof TypeError) {
    return "Could not reach storage. Check your connection and try again; nothing was changed.";
  }
  return "The deploy failed. Nothing was published, so the live site is unchanged.";
}
